import {
  VERIFICATION_STATUS_LABELS,
  type CustomerProblem,
} from '@shared/types/customer-problem';

export interface IssueTagOption {
  value: string;
  problemName: string;
  score: number;
  verificationStatusLabel: string;
  denied: boolean;
  label: string;
}

export function confidenceToScore(confidence: number): number {
  return Math.round(Math.max(0, Math.min(1, confidence)) * 100);
}

export function buildIssueTagOption(
  customerProblem: CustomerProblem,
  problemNameById: Map<string, string>,
): IssueTagOption {
  const problemName =
    problemNameById.get(customerProblem.problem_id) ?? customerProblem.problem_id;
  const score = confidenceToScore(customerProblem.confidence);
  const verificationStatusLabel =
    VERIFICATION_STATUS_LABELS[customerProblem.verification_status] ??
    customerProblem.verification_status;
  return {
    value: customerProblem.id,
    problemName,
    score,
    verificationStatusLabel,
    denied: customerProblem.verification_status === 'denied',
    label: problemName,
  };
}

export function buildIssueTagOptions(
  customerProblems: CustomerProblem[],
  problemNameById: Map<string, string>,
): IssueTagOption[] {
  return [...customerProblems]
    .sort((a, b) => b.confidence - a.confidence)
    .map((cp) => buildIssueTagOption(cp, problemNameById));
}

export function findIssueTagOption(
  options: IssueTagOption[],
  value: string,
): IssueTagOption | undefined {
  if (value === '') return undefined;
  return options.find((option) => option.value === value);
}

/** 課題セレクトの最小 option 形 (`IssueTagOption` はこれの上位互換)。 */
export interface IssueTagSelectOption {
  value: string;
  label: string;
}

/**
 * 現在値が候補外でもセレクトに残す (86eya0dva PR3)。read 統一後は行の参照課題が
 * 画面ターゲットの通常候補 (firm 課題 xor lawyer 課題) の外にあり得る。候補集合
 * 自体は変えず、現在値だけを先頭に pin して「(なし)」と区別できる表示にする。
 * ラベルは一覧応答同梱の課題名 (`source_problem.problem_name`) を使う。
 */
export function withCurrentOption<T extends IssueTagSelectOption>(
  options: T[],
  currentId: string | null,
  currentLabel: string | null,
): (T | IssueTagSelectOption)[] {
  if (!currentId || currentId === '') return options;
  if (options.some((option) => option.value === currentId)) return options;
  return [
    { value: currentId, label: currentLabel ?? '(候補外の課題)' },
    ...options,
  ];
}
